<script setup lang="ts">
/**
 * The image editor's colour row, as WhatsApp Web's: eight colours, an optional "none", and a
 * caret that opens the spectrum with the colour's code. Drawing, text and shapes share it.
 */
import { computed, nextTick, ref, watch } from "vue";
import Icon from "./Icon.vue";
import { COLORS } from "../composables/imageEdit";
import { useEscapeLayer } from "../composables/escape";
import { t } from "../i18n";

const props = defineProps<{ allowNone?: boolean; compact?: boolean }>();
const model = defineModel<string | null>({ required: true });

const open = ref(false);
const spectrum = ref<HTMLCanvasElement>();
const hex = ref("");
const marker = ref<{ x: number; y: number } | null>(null);
const custom = computed(
  () =>
    model.value !== null &&
    !COLORS.some((c) => c.hex === model.value?.toLowerCase()),
);

const W = 300,
  H = 150;
function paint() {
  const c = spectrum.value;
  if (!c) return;
  const ctx = c.getContext("2d", { willReadFrequently: true })!;
  const hue = ctx.createLinearGradient(0, 0, W, 0);
  [
    "#ff0000",
    "#ffff00",
    "#00ff00",
    "#00ffff",
    "#0000ff",
    "#ff00ff",
    "#ff0000",
  ].forEach((color, i, all) => hue.addColorStop(i / (all.length - 1), color));
  ctx.fillStyle = hue;
  ctx.fillRect(0, 0, W, H);
  const white = ctx.createLinearGradient(0, 0, 0, H / 2);
  white.addColorStop(0, "rgba(255,255,255,1)");
  white.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = white;
  ctx.fillRect(0, 0, W, H / 2);
  const black = ctx.createLinearGradient(0, H / 2, 0, H);
  black.addColorStop(0, "rgba(0,0,0,0)");
  black.addColorStop(1, "rgba(0,0,0,1)");
  ctx.fillStyle = black;
  ctx.fillRect(0, H / 2, W, H / 2);
}

function pick(e: PointerEvent) {
  const c = spectrum.value;
  if (!c) return;
  const r = c.getBoundingClientRect();
  const x = Math.min(W - 1, Math.max(0, ((e.clientX - r.left) / r.width) * W));
  const y = Math.min(H - 1, Math.max(0, ((e.clientY - r.top) / r.height) * H));
  const [red, green, blue] = c
    .getContext("2d", { willReadFrequently: true })!
    .getImageData(Math.round(x), Math.round(y), 1, 1).data;
  marker.value = { x: (x / W) * 100, y: (y / H) * 100 };
  model.value =
    "#" +
    [red, green, blue].map((v) => v!.toString(16).padStart(2, "0")).join("");
}

function drag(e: PointerEvent) {
  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  pick(e);
}

function typed() {
  const v = hex.value.trim().replace(/^#?/, "#");
  if (/^#[0-9a-f]{6}$/i.test(v)) model.value = v.toLowerCase();
}

watch(open, async (v) => {
  if (!v) return;
  marker.value = null;
  await nextTick();
  paint();
});
watch(model, (v) => (hex.value = (v ?? "").toUpperCase()), { immediate: true });
useEscapeLayer(open, () => (open.value = false));
</script>

<template>
  <div class="editor-colors" :class="{ compact: props.compact }">
    <button
      v-if="allowNone"
      type="button"
      class="editor-color none"
      :aria-label="t('editor.no_color')"
      :aria-pressed="model === null"
      @click="model = null"
    >
      <Icon name="wa-block" :size="22" />
    </button>
    <button
      v-for="c in COLORS"
      :key="c.id"
      type="button"
      class="editor-color"
      :aria-label="t(`editor.color.${c.id}`)"
      :aria-pressed="model === c.hex"
      @click="model = c.hex"
    >
      <span :style="{ background: c.hex }" />
    </button>
    <span class="editor-colors-more">
      <button
        type="button"
        class="editor-caret"
        :class="{ open }"
        :aria-label="t('editor.more_colors')"
        :aria-expanded="open"
        @click="open = !open"
      >
        <Icon name="wa-chevron" :size="24" />
      </button>
      <Transition name="pop"
        ><div
          v-if="open"
          class="editor-spectrum"
          role="dialog"
          :aria-label="t('editor.more_colors')"
        >
          <div class="editor-spectrum-field">
            <canvas
              ref="spectrum"
              :width="W"
              :height="H"
              @pointerdown="drag"
              @pointermove="(e) => e.buttons && pick(e)"
            />
            <span
              v-if="marker"
              class="editor-spectrum-marker"
              :style="{
                left: marker.x + '%',
                top: marker.y + '%',
                background: model ?? 'transparent',
              }"
            />
          </div>
          <label class="editor-spectrum-code">
            <span
              class="editor-spectrum-swatch"
              :class="{ custom }"
              :style="{ background: model ?? 'transparent' }"
            />
            <input
              v-model="hex"
              maxlength="7"
              spellcheck="false"
              :aria-label="t('editor.hex')"
              @change="typed"
              @keydown.enter.prevent="typed"
            />
          </label></div
      ></Transition>
    </span>
  </div>
</template>
